import * as QueryString from "querystring";
import {
  API_ACCEPT,
  APP_NAME,
  AuthApiPath,
  CONTENT_TYPE_FORM,
  REQUIRES_SLASH,
} from "@/lib/auth/constants";
import newrelic from "@/lib/monitoring/newrelic";
import { getAuthQueryParams } from "@/lib/auth/getAuthQueryParams";
import { buildApiEndpoint } from "@/lib/auth/buildApiEndpoint";

type TruthyFalsy = "true" | "1" | "false" | "0";

interface RegistrationFormParams {
  // Required fields
  email: string;
  password: string;
  name: string;

  // Optional fields (per OSS Code; Some might be required by 2U)
  username?: string;
  country: string; // 2U Req'd
  honor_code: TruthyFalsy; // 2U Req'd
  terms_of_service: TruthyFalsy; // 2U Req'd
  marketing_emails_opt_in?: TruthyFalsy;

  // Course-related fields
  course_id?: string;

  // Third party auth fields
  provider?: string;
  access_token?: string;
  social_auth_provider?: string;

  // Analytics and tracking fields
  total_registration_time?: string;
  host?: string;
  app_name?: string; // Added by the registration call
  utm_campaign?: string;

  // Extended profile fields (if enabled)
  year_of_birth?: string;
  level_of_education?: string;
  gender?: string;
  goals?: string;
  mailing_address?: string;

  // Language preference
  language?: string;

  // reCAPTCHA v3 token
  captcha_token?: string;
}

interface RegistrationResponse {
  success: boolean;
}

export interface RegistrationSuccessResponse extends RegistrationResponse {
  success: true;
  authenticated_user: {
    username: string;
    full_name: string;
    user_id: number;
  };
  redirect_url: string;
}

interface RegistrationErrorBaseResponse extends RegistrationResponse {
  success: false;
  error_code: string;
}

export interface RegistrationErrorResponse
  extends RegistrationErrorBaseResponse {
  [field: string]:
    | Array<{ user_message: string }>
    | string
    | boolean
    | undefined;
}

interface RateLimitErrorResponse extends RegistrationErrorBaseResponse {
  error_code: "forbidden-request";
}

interface ThirdPartyAuthRequiredErrorResponse
  extends RegistrationErrorBaseResponse {
  error_code: "third-party-auth-required";
}

/**
 *  Union type of Registration responses
 *
 * > Note: This is generated from LMS's RegistrationView @ openedx/core/djangoapps/user_authn/views/register.py
 */
export type RegistrationViewResponse =
  | RegistrationSuccessResponse
  | RegistrationErrorResponse
  | RateLimitErrorResponse
  | ThirdPartyAuthRequiredErrorResponse;

export function isSuccessResponse(
  response: RegistrationViewResponse,
): response is RegistrationSuccessResponse {
  return response.success && "authenticated_user" in response;
}

export function isErrorResponse(
  response: RegistrationViewResponse,
): response is RegistrationErrorResponse {
  return (
    "error_code" in response && response.error_code !== "forbidden-request"
  );
}

export function isRateLimitError(
  response: RegistrationViewResponse,
): response is RateLimitErrorResponse {
  return (
    (response as RegistrationErrorBaseResponse).error_code ===
    "forbidden-request"
  );
}

export function isThirdPartyAuthRequired(
  response: RegistrationViewResponse,
): response is ThirdPartyAuthRequiredErrorResponse {
  return (
    (response as RegistrationErrorBaseResponse).error_code ===
    "third-party-auth-required"
  );
}

/**
 * Register a Standard User with the LMS
 * @param user User Data
 * @see {isSuccessResponse} To determine if Registration was Successful
 * @see {isErrorResponse} To Determine if there was a Field Error
 * @see {isRateLimitError} To determine if we were Rate Limited
 * @see {isThirdPartyAuthRequired} To determine if some sort of Third Party auth is required (unsupported for now)
 */
export default async function registerUser(
  user: RegistrationFormParams,
): Promise<RegistrationViewResponse> {
  const startTime = Date.now();
  try {
    const registrationInfo = {
      app_name: APP_NAME,
      ...user,
      ...getAuthQueryParams(),
    };
    const headers = {
      accept: API_ACCEPT,
      "cache-control": "no-cache",
      pragma: "no-cache",
      "Content-Type": CONTENT_TYPE_FORM,
    } as Record<string, string>;

    /*
     * Returns:
     *  HttpResponse: 200 on success
     *  HttpResponse: 400 if the request is not valid.
     *  HttpResponse: 409 if an account with the given username or email
     *    address already exists
     *  HttpResponse: 403 operation not allowed
     *
     *  based on openedx/core/djangoapps/user_authn/views/register.py :
     */
    const res = await fetch(
      buildApiEndpoint(AuthApiPath.RegUser, {
        endingSlash: REQUIRES_SLASH.includes(AuthApiPath.RegUser),
      }),
      {
        headers: {
          ...headers,
        },
        body: QueryString.stringify(registrationInfo),
        method: "POST",
        credentials: "include",
      },
    );
    const result = await res.json();

    // Type assertion based on status code
    if (res.status === 200) {
      return result as RegistrationSuccessResponse;
    } else if (res.status === 403) {
      if (result.error_code === "forbidden-request") {
        return { ...result, success: false } as RateLimitErrorResponse;
      }
      return {
        ...result,
        success: false,
      } as ThirdPartyAuthRequiredErrorResponse;
    } else {
      if (result.error_code) {
        return { ...result, success: false } as RegistrationErrorResponse;
      } else {
        return {
          ...result,
          success: false,
          error_code: "unknown",
        } as RegistrationErrorResponse;
      }
    }
  } catch (e) {
    const responseTime = Date.now() - startTime;
    newrelic.noticeError(e as Error, {
      submodule: "auth",
      endpoint: "UserRegistration",
      context: "networkError",
      responseTime: responseTime,
      timestamp: new Date().toISOString(),
    });
    throw e;
  }
}
